/**
 * KA-49 §4.3 — LUẬT "TIẾN TRÌNH LỆNH 4 BƯỚC" (hàm THUẦN, không React — `kiem-ka49` nhập thẳng):
 *
 *   1 Yêu cầu          lệnh đã tạo (ai, mức, giờ)
 *   2 Đã phát ra mesh  `cmd_ack` của host cho đèn này: 0 (hoặc 6/7/8 — host đã phát rồi mới báo đèn hỏng)
 *   3 Đèn báo mức mới  một `sensor_row` có `dim` = mức muốn, SAU giờ lệnh
 *   4 Công suất cập nhật  một `sensor_row` nữa SAU dòng của bước 3
 *
 * 🔴 `cmd_ack` 0 KHÔNG BAO GIỜ làm xong bước 3. Báo nhận của host chỉ xác nhận "đã phát ra mesh"; đèn đổi thật hay chưa chỉ
 *   có số đo đèn tự báo mới nói được (bộ phá của kiem-ka49 (a): coi ack 0 là "đã áp" ⇒ phép đỏ).
 * ⛔ Luật số 0: chưa có dòng số đo ⇒ "đang chờ" (vàng + đếm giây), không bao giờ "đạt".
 */

/** Nghĩa mã `cmd_ack.status` — CÙNG khoá với `MA_CMD_ACK` của máy chủ (`nemaIntake.ts`; kiem-ka49 so hai bên). */
export const MA_CMD_ACK: Record<number, string> = {
  0: 'Đã phát ra mesh', 4: 'Host báo gói sai', 5: 'Host chưa sẵn sàng', 6: 'Đèn không trả lời', 7: 'Đèn trả lời sai mức (lần 3)', 8: 'Host ngừng thử sau 10 lần',
};
/** Mã nói "host ĐÃ phát, đèn hỏng" — bước 2 xong, bước 3 lỗi. */
const MA_DEN_HONG = new Set([6, 7, 8]);

export interface AckTho { addr: number; status: number; ts: string }
export interface DongSoTho { ts: string; dim: number | null; p_w: number }
export interface LenhTienTrinh {
  id: string; kind: 'dim' | 'tra_ve_lich'; level: number | null; issuedAt: string; state: string; ackAt: string | null;
  nguoi: string | null; dich: number[]; ack: AckTho[]; so: Record<string, DongSoTho[]>;
}
export interface TienTrinhTu { now: string; lenh: LenhTienTrinh[] }

export type TrangThaiBuoc = 'xong' | 'cho' | 'loi' | 'chua';
export interface Buoc { so: 1 | 2 | 3 | 4; ten: string; tt: TrangThaiBuoc; phu: string; luc: string | null }
/** Kết quả một đèn — cột "Trạng thái" của bảng đèn. */
export type KetQuaDen = 'DAT' | 'CHO' | 'KHONG_TRA_LOI' | 'SAI_MUC' | 'HOST_NGUNG' | 'TU_CHOI';
export const NHAN_KET_QUA: Record<KetQuaDen, { nhan: string; tone: 'green' | 'amber' | 'red' | 'slate' }> = {
  DAT: { nhan: 'Đạt', tone: 'green' }, CHO: { nhan: 'Đang chờ', tone: 'amber' }, KHONG_TRA_LOI: { nhan: 'Không trả lời', tone: 'red' },
  SAI_MUC: { nhan: 'Sai mức', tone: 'red' }, HOST_NGUNG: { nhan: 'Host ngừng thử', tone: 'red' }, TU_CHOI: { nhan: 'Không phát được', tone: 'red' },
};
export const TEN_BUOC = ['Yêu cầu', 'Đã phát ra mesh', 'Đèn báo mức mới', 'Công suất cập nhật'] as const;

const gio = (iso: string) => { const d = new Date(iso); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`; };
const giay = (a: string, b: string) => Math.max(0, Math.round((new Date(b).getTime() - new Date(a).getTime()) / 1000));
const sGon = (s: number) => (s < 60 ? `${s} s` : `${Math.floor(s / 60)} ph ${s % 60} s`);

/** Tiến trình 4 bước của MỘT đèn trong MỘT lệnh dim. */
export function tienTrinhDen(l: LenhTienTrinh, addr: number, now: string, tenDen = `Đèn ${addr.toString(16).toUpperCase()}`): { buoc: Buoc[]; ket: KetQuaDen; choGiay: number | null } {
  const muc = l.level;
  const b1: Buoc = { so: 1, ten: TEN_BUOC[0], tt: 'xong', phu: `${tenDen} · đặt ${muc ?? '—'} %${l.nguoi ? ` · bởi ${l.nguoi}` : ''}`, luc: gio(l.issuedAt) };
  const acks = l.ack.filter((a) => a.addr === addr);
  const ack0 = acks.find((a) => a.status === 0) ?? null;
  const hong = [...acks].reverse().find((a) => MA_DEN_HONG.has(a.status)) ?? null;
  const tuChoi = !ack0 && !hong ? acks.find((a) => a.status !== 0) ?? null : null;
  const daPhat = ack0 ?? hong;

  let b2: Buoc;
  if (daPhat) b2 = { so: 2, ten: TEN_BUOC[1], tt: 'xong', phu: `Host báo nhận sau ${sGon(giay(l.issuedAt, daPhat.ts))}`, luc: gio(daPhat.ts) };
  else if (tuChoi) b2 = { so: 2, ten: TEN_BUOC[1], tt: 'loi', phu: MA_CMD_ACK[tuChoi.status] ?? `Host báo mã ${tuChoi.status}`, luc: gio(tuChoi.ts) };
  else if (l.state === 'HET_HAN' || l.state === 'ACK_LOI') b2 = { so: 2, ten: TEN_BUOC[1], tt: 'loi', phu: l.state === 'HET_HAN' ? 'Tủ không hồi đáp' : 'Tủ từ chối lệnh', luc: l.ackAt ? gio(l.ackAt) : null };
  else b2 = { so: 2, ten: TEN_BUOC[1], tt: 'cho', phu: 'Chờ host báo nhận', luc: null };

  /* bước 3: CHỈ số đo đèn tự báo (dim = mức muốn, sau giờ lệnh) — ack 0 không làm xong bước này */
  const so = (l.so[String(addr)] ?? []).filter((r) => r.ts > l.issuedAt);
  const i3 = muc === null ? -1 : so.findIndex((r) => r.dim === muc);
  const r3 = i3 >= 0 ? so[i3] : null;
  const cuoi = so.length ? so[so.length - 1] : null;
  let b3: Buoc;
  let ket: KetQuaDen;
  if (r3) { b3 = { so: 3, ten: TEN_BUOC[2], tt: 'xong', phu: `Đèn báo ${r3.dim} %`, luc: gio(r3.ts) }; ket = 'DAT'; }
  else if (hong) {
    b3 = { so: 3, ten: TEN_BUOC[2], tt: 'loi', phu: `${MA_CMD_ACK[hong.status]}${cuoi ? ` · đèn báo ${cuoi.dim ?? '—'} %` : ''}`, luc: gio(hong.ts) };
    ket = hong.status === 6 ? 'KHONG_TRA_LOI' : hong.status === 7 ? 'SAI_MUC' : 'HOST_NGUNG';
  } else if (b2.tt === 'loi') { b3 = { so: 3, ten: TEN_BUOC[2], tt: 'chua', phu: '—', luc: null }; ket = 'TU_CHOI'; }
  else {
    b3 = { so: 3, ten: TEN_BUOC[2], tt: daPhat ? 'cho' : 'chua', phu: cuoi ? `Đèn còn báo ${cuoi.dim ?? '—'} %` : 'Chờ đèn báo số đo', luc: null };
    ket = 'CHO';
  }

  let b4: Buoc;
  const r4 = i3 >= 0 ? so[i3 + 1] ?? null : null;
  if (r4) b4 = { so: 4, ten: TEN_BUOC[3], tt: 'xong', phu: `P = ${r4.p_w} W`, luc: gio(r4.ts) };
  else if (r3) b4 = { so: 4, ten: TEN_BUOC[3], tt: 'cho', phu: `Lấy lại sau bước 3 · lúc đạt P = ${r3.p_w} W`, luc: null };
  else b4 = { so: 4, ten: TEN_BUOC[3], tt: 'chua', phu: '—', luc: null };

  const choGiay = ket === 'CHO' ? giay(l.issuedAt, now) : null;
  return { buoc: [b1, b2, b3, b4], ket, choGiay };
}

/** Lệnh gần nhất (dim hoặc trả về lịch) chạm tới đèn `addr` trong danh sách lệnh của TỦ (mới trước). */
export function lenhGanNhatCuaDen(ds: readonly LenhTienTrinh[], addr: number): LenhTienTrinh | null {
  let best: LenhTienTrinh | null = null;
  for (const l of ds) if (l.dich.includes(addr) && (!best || l.issuedAt > best.issuedAt)) best = l;
  return best;
}

/** Cột "Muốn" + "Trạng thái" của bảng đèn: lệnh gần nhất của đèn. Không có lệnh ⇒ null (bảng vẽ "—", luật số 0). */
export function muonCuaDen(ds: readonly LenhTienTrinh[] | null, addr: number, now: string): { muon: string; muc: number | null; ket: KetQuaDen | null } | null {
  if (!ds) return null;
  const l = lenhGanNhatCuaDen(ds, addr);
  if (!l) return null;
  if (l.kind === 'tra_ve_lich') return { muon: 'Lịch', muc: null, ket: null };
  return { muon: `${l.level ?? '—'} %`, muc: l.level, ket: tienTrinhDen(l, addr, now).ket };
}

/**
 * Lệnh NHÓM / TỦ / TUYẾN: bước 3–4 thành "x / y đèn đạt". `lenh` = các lệnh (mỗi tủ một) được phát cùng lúc.
 * Trả về tiến trình gộp (bước 1–2 lấy theo đa số) và danh sách từng đèn để mở ra.
 */
export function tienTrinhNhieu(lenh: readonly { l: LenhTienTrinh; tenTu: string; ten: (addr: number) => string }[], now: string) {
  const den = lenh.flatMap(({ l, tenTu, ten }) => l.dich.map((addr) => ({ addr, tenTu, ten: ten(addr), cabinetLenh: l.id, ...tienTrinhDen(l, addr, now, ten(addr)) })));
  const tong = den.length;
  const dem = (i: number, tt: TrangThaiBuoc) => den.filter((d) => d.buoc[i].tt === tt).length;
  const gop = (i: 1 | 2 | 3, ten: string): Buoc => {
    const xong = dem(i, 'xong'), loi = dem(i, 'loi');
    const tt: TrangThaiBuoc = xong === tong ? 'xong' : loi > 0 && xong + loi === tong ? 'loi' : xong + loi > 0 || dem(i, 'cho') > 0 ? 'cho' : 'chua';
    return { so: (i + 1) as 2 | 3 | 4, ten, tt, phu: `${xong} / ${tong} đèn đạt${loi ? ` · ${loi} lỗi` : ''}`, luc: null };
  };
  const dau = lenh[0]?.l;
  const b1: Buoc = { so: 1, ten: TEN_BUOC[0], tt: 'xong', phu: `${tong} đèn · đặt ${dau?.level ?? '—'} %${dau?.nguoi ? ` · bởi ${dau.nguoi}` : ''}`, luc: dau ? gio(dau.issuedAt) : null };
  const buoc = [b1, gop(1, TEN_BUOC[1]), gop(2, TEN_BUOC[2]), gop(3, TEN_BUOC[3])];
  const choGiay = buoc[2].tt === 'cho' && dau ? giay(dau.issuedAt, now) : null;
  return { buoc, den, tong, dat: den.filter((d) => d.ket === 'DAT').length, choGiay };
}
